import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/editorial-policy")({
  head: () => ({
    meta: [
      { title: "Editorial Policy | Acadly.in" },
      {
        name: "description",
        content:
          "Acadly.in's editorial standards, sourcing rules, corrections process, and funding disclosure. Written for readers and Google News reviewers.",
      },
      { property: "og:title", content: "Editorial Policy | Acadly.in" },
      {
        property: "og:description",
        content:
          "How Acadly.in writes, verifies, corrects, and funds its education coverage.",
      },
      { property: "og:url", content: "https://acadly.in/editorial-policy" },
    ],
    links: [{ rel: "canonical", href: "https://acadly.in/editorial-policy" }],
  }),
  component: EditorialPolicyPage,
});

function EditorialPolicyPage() {
  return (
    <div className="mx-auto max-w-3xl px-5 py-16 md:py-20 prose-post">
      <div className="stamp mb-6">Editorial Policy</div>
      <h1
        className="text-5xl md:text-6xl leading-[1] tracking-tight font-semibold"
        style={{ fontFamily: "var(--font-display)" }}
      >
        Editorial Policy
      </h1>
      <p className="mono-label !mt-2">Last updated: 25 July 2026</p>

      <p>
        This page explains how Acadly.in produces, verifies, and corrects its
        content. It exists so every reader can understand the standards behind
        an article before trusting it, and so platforms like Google News can
        review the site against their publisher guidelines.
      </p>

      <h2>Mission and audience</h2>
      <p>
        Acadly.in is an independent education publication for Indian college
        students, with a particular focus on the University of Delhi, economics,
        and exam preparation. The mission is to publish clear, accurate, and
        useful study guidance that helps a student read smarter, write better
        answers, and avoid common mistakes.
      </p>
      <p>
        The site is not a coaching institute, a tuition provider, or a degree-
        granting body. It does not enrol students, conduct live classes, or issue
        certificates.
      </p>

      <h2>Ownership and authorship</h2>
      <p>
        Acadly.in is owned and edited by Dhairya, a Delhi University economics
        graduate. The founder is named on the{" "}
        <Link to="/about">About page</Link>, and every article carries a visible
        byline. Guest articles are rare; when published, they are clearly
        attributed to the named author and include a short bio. No articles are
        published anonymously.
      </p>

      <h2>Editorial standards</h2>
      <p>
        Every article is written or substantially rewritten by a human editor and
        read end to end before publication. We do not publish raw AI output.
        Drafts may begin with an outline or research support from AI tools, but
        the final copy is reviewed for accuracy, tone, and clarity.
      </p>
      <p>Before publication, we aim to confirm that:</p>
      <ul>
        <li>
          Facts, dates, formulas, and policy references are checked against a
          current source.
        </li>
        <li>
          University rules, syllabi, or admission deadlines are sourced from an
          official document or press release where possible.
        </li>
        <li>
          Every substantive claim has a citation, link, or clear source note.
        </li>
        <li>
          The headline and introduction accurately reflect the body of the
          article.
        </li>
      </ul>

      <h2>Sourcing and attribution</h2>
      <p>
        Articles rely on a mix of primary sources, official documents, and
        established secondary references. When a post depends on a rule, date,
        policy, or official announcement, we link to the original source or name
        the document. If a source is offline, we describe it in enough detail
        that a reader can locate it independently.
      </p>
      <p>
        News articles carry a clear dateline. The published date reflects when the
        article was made live on the site, not necessarily when the event occurred.
      </p>

      <h2>How we verify News</h2>
      <p>
        News articles about Delhi University are held to a stricter sourcing
        bar than explainer or study-strategy pieces. Before a News article is
        published:
      </p>
      <ul>
        <li>
          Every specific number, deadline, or policy claim is linked inline to
          the source we are relying on. Where a primary document is public
          (a DU notification, a PIB release, an Academic Council minute, the
          CSAS portal), we link to that. Where only secondary reporting is
          available, we link to the reporting and say so in the text.
        </li>
        <li>
          Admission dates, allotment counts, and deadlines are cross-checked
          against the official DU portal at{" "}
          <a href="https://admission.uod.ac.in/" target="_blank" rel="noopener noreferrer">
            admission.uod.ac.in
          </a>{" "}
          or{" "}
          <a href="https://www.du.ac.in/" target="_blank" rel="noopener noreferrer">
            du.ac.in
          </a>{" "}
          wherever the university has published them. If a figure cannot be
          confirmed against an official source, we attribute it to the
          reporting we saw and tell the reader to check the portal.
        </li>
        <li>
          Corrections to News articles are issued as soon as we become aware
          of an error, with a visible note at the bottom of the piece. See
          the <Link to="/corrections">Corrections page</Link> for how to flag
          one.
        </li>
        <li>
          We do not batch-publish News articles same-day to inflate an
          archive. Each News piece is published on the day its reporting is
          verified.
        </li>
      </ul>

      <h2>Corrections</h2>
      <p>
        We correct factual errors as soon as we become aware of them. A corrected
        article receives a visible note at the bottom of the page stating what
        changed and when. The full corrections policy and contact details are on
        the <Link to="/corrections">Corrections page</Link>.
      </p>

      <h2>Advertising and funding</h2>
      <p>
        Acadly.in does not currently carry advertising or sponsored content.
        If advertising is introduced in future, including through Google
        AdSense, it will be clearly disclosed and kept separate from editorial
        content. We do not accept sponsored posts, paid placements, or
        affiliate arrangements at present. If that ever changes, any paid
        relationship will be disclosed at the top of the affected article, and
        the cookie and tracking implications will be set out in the{" "}
        <Link to="/privacy-policy">Privacy Policy</Link> before ads go live.
      </p>

      <h2>Independence and conflicts of interest</h2>
      <p>
        Editorial coverage is independent of advertisers and partners. We do not
        let ad relationships influence topic selection, framing, or conclusions.
        If a writer has a financial, academic, or personal connection to a
        subject, it is disclosed in the article.
      </p>

      <h2>Content updates and freshness</h2>
      <p>
        University rules, exam patterns, and government policies change often.
        Older articles are reviewed periodically and updated when the underlying
        facts change. An article that has been updated shows a "last updated"
        date. If an article has not been reviewed recently, the original
        publication date remains visible.
      </p>

      <h2>Contact the editorial team</h2>
      <p>
        Questions, corrections, source requests, and story ideas are welcome.
        Please write to <a href="mailto:hello@acadly.in">hello@acadly.in</a> or
        use the form on the <Link to="/contact">Contact page</Link>. We aim to
        respond to substantive corrections within 48 hours.
      </p>
    </div>
  );
}
